import { useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, Users, Ruler, Gem, X } from "lucide-react";

// Media slots resolve from /public/images/projects/ first, stock as fallback.
const VALUES = [
  {
    id: "quality",
    icon: ShieldCheck,
    title: "QUALITY ASSURED",
    sub: "Fully licensed NSW Master Builder — Lic. 476816C.",
    color: "#00E676",
    modalTitle: "Uncompromising Craftsmanship, Engineering Integrity & Full NSW Compliance",
    modalSub: "Built under Master Builder Licence 476816C to strict Australian Standards.",
    sections: [
      {
        heading: "Engineering Rigor & Standards",
        body: "At JAI HOMES, quality is not a buzzword—it is an engineered standard. Every project, from bespoke residential residences to multi-level commercial fit-outs, strictly adheres to the National Construction Code (NCC) and Australian Standards (AS). We conduct multi-stage third-party engineering inspections covering footings, timber framing, waterproofing membranes, and structural steel compliance before signing off on any construction milestone.",
      },
      {
        heading: "Material Superiority",
        body: "We partner exclusively with certified Australian material suppliers. From termite-resistant framing timber and acoustic dampening drywall to commercial-grade safety glass and high-durability external claddings, every element is selected to withstand the harshest Australian climatic conditions.",
      },
      {
        heading: "Certifications & Warranties",
        body: "Complete peace of mind is built into every contract. We handle end-to-end certification requirements—including Development Application (DA) approvals, Complying Development Certificates (CDC), and final Occupation Certificates (OC)—supported by comprehensive statutory home building warranties and structural guarantees.",
      },
    ],
    media: "value-quality.jpg",
    mediaFallback: "https://images.pexels.com/photos/2119712/pexels-photo-2119712.jpeg?auto=compress&cs=tinysrgb&w=1200",
    mediaAlt: "Structural framework and quality inspection on a JAI HOMES site",
  },
  {
    id: "communities",
    icon: Users,
    title: "THRIVING COMMUNITIES",
    sub: "Multi-unit estates & childcare centres for local families.",
    color: "#FFC107",
    modalTitle: "Purpose-Built Social Infrastructure & Connected Living Environments",
    modalSub: "Engineering spaces that support Australian families, early education, and neighborhood growth.",
    sections: [
      {
        heading: "Turnkey Childcare Infrastructure",
        body: "As specialists in turnkey childcare asset development across NSW growth corridors, we design early learning facilities that balance strict regulatory compliance with inviting, stimulating environments. Our childcare builds incorporate dedicated acoustic boundary barriers, optimized safe drop-off/pick-up zones, non-toxic outdoor play spaces, and council-compliant safety barriers designed for maximum operational efficiency.",
      },
      {
        heading: "Masterplanned Multi-Unit Communities",
        body: "Our residential developments and gated estates focus on human-centric urban design. We incorporate landscaped communal gardens, solar-ready energy infrastructure, wide access driveways, and private alfresco areas that encourage neighborly interaction while preserving individual privacy.",
      },
      {
        heading: "Local NSW Impact",
        body: "By focusing on key population hubs—including Western Sydney, South West growth corridors, the Illawarra, and the Hunter region—our projects directly support local council infrastructure goals, creating essential community hubs and boosting surrounding property values.",
      },
    ],
    media: "value-communities.jpg",
    mediaFallback: "https://images.pexels.com/photos/3662667/pexels-photo-3662667.jpeg?auto=compress&cs=tinysrgb&w=1200",
    mediaAlt: "Completed childcare play space and community streetscape",
  },
  {
    id: "design",
    icon: Ruler,
    title: "THOUGHTFUL DESIGN",
    sub: "Medical fit-outs, retail & indoor-outdoor architecture.",
    color: "#00B0FF",
    modalTitle: "Modern Australian Architecture, Climate Adaptability & Spatial Ergonomics",
    modalSub: "Seamless indoor-outdoor integration designed around how people actually live and work.",
    sections: [
      {
        heading: "Climate-Adaptive Architecture",
        body: "Australian living demands intelligent climate design. Our floorplans maximize cross-flow ventilation, optimize natural passive solar orientation to reduce summer heat loads, and incorporate energy-efficient double glazing, thermal insulation, and integrated shade structures.",
      },
      {
        heading: "Commercial & Healthcare Precision",
        body: "For retail shop fit-outs and medical facilities, spatial layout dictates business profitability. We engineer commercial floorplans for optimal traffic flow, DDA disability access compliance, hygienic washable surfaces, custom cabinetry integration, and specialized utility feeds (oxygen, dental air, high-voltage clinical supply).",
      },
      {
        heading: "Bespoke Residential Elegance",
        body: "Our custom residential homes and knock-down rebuilds feature open-plan living zones, high ceiling clearance, seamless floor-to-ceiling glass transitions to alfresco dining areas, and hidden utility spaces designed for clutter-free, luxurious family living.",
      },
    ],
    media: "value-design.jpg",
    mediaFallback: "https://images.pexels.com/photos/1571468/pexels-photo-1571468.jpeg?auto=compress&cs=tinysrgb&w=1200",
    mediaAlt: "Architectural interior living space with natural light",
  },
  {
    id: "value",
    icon: Gem,
    title: "ENDURING VALUE",
    sub: "Turnkey assets & knock-down rebuilds across NSW.",
    color: "#FF1744",
    modalTitle: "Capital Preservation, Optimized Commercial Yields & Asset Longevity",
    modalSub: "Strategic construction designed for institutional investors, smart landowners, and long-term capital growth.",
    sections: [
      {
        heading: "Investor Yield Optimization",
        body: "We build assets designed to generate strong, sustainable cash flow. Whether constructing a high-yield turnkey childcare facility leased to national operators or developing multi-unit residential duplexes, we analyze yield metrics, build costs, and rental returns to ensure maximum investment return on equity.",
      },
      {
        heading: "Land Value Maximization (Joint Ventures & Rebuilds)",
        body: "Unlocking hidden value in NSW land is our specialty. Through strategic knock-down & rebuild solutions or Joint Venture (JV) land development structures, we turn underutilized single residential blocks or larger land parcels into high-density, multi-tenant revenue-generating assets.",
      },
      {
        heading: "Low-Maintenance Construction",
        body: "Asset longevity depends on minimizing long-term operational and maintenance overheads. We specify low-upkeep exterior facades, commercial-grade HVAC systems, durable flooring systems, and weather-resistant roofing materials to protect long-term capital value and lower life-cycle repair costs.",
      },
    ],
    media: "value-value.jpg",
    mediaFallback: "https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg?auto=compress&cs=tinysrgb&w=1200",
    mediaAlt: "Finished high-end villa with premium facade",
  },
];

const GLOWS = {
  quality: ["#059669", "#10B981"],
  communities: ["#D97706", "#F59E0B"],
  design: ["#0891B2", "#06B6D4"],
  value: ["#DC2626", "#EF4444"],
};

const MediaSlot = ({ local, fallback, alt, color }) => {
  const [src, setSrc] = useState(`/images/projects/${local}`);
  return (
    <div className="relative h-44 rounded-2xl overflow-hidden mb-6 border border-white/10" data-testid="topbar-modal-media">
      <img
        src={src}
        alt={alt}
        loading="lazy"
        onError={() => src !== fallback && setSrc(fallback)}
        className="w-full h-full object-cover"
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: `linear-gradient(to top, #0A0E17CC, transparent 55%), radial-gradient(60% 60% at 50% 100%, ${color}22, transparent 70%)` }}
      />
    </div>
  );
};

export const TopBar = () => {
  const [active, setActive] = useState(null);

  return (
    <>
      <div className="relative z-[100] grid grid-cols-2 lg:grid-cols-4 border-b border-white/10" data-testid="nsw-values-topbar">
        {VALUES.map((v) => {
          const Icon = v.icon;
          return (
            <button
              key={v.id}
              data-testid={`topbar-${v.id}`}
              onClick={() => setActive(v)}
              onMouseEnter={() => setActive(v)}
              className="group flex items-center gap-3 px-4 py-2.5 border-t-2 text-left transition-all duration-300 hover:-translate-y-0.5 cursor-pointer"
              style={{
                borderTopColor: v.color,
                background: `linear-gradient(160deg, ${GLOWS[v.id][0]}38, ${GLOWS[v.id][1]}1F)`,
                boxShadow: `inset 0 -18px 30px -18px ${GLOWS[v.id][0]}55`,
              }}
            >
              <Icon
                size={18}
                strokeWidth={2.2}
                style={{ color: v.color }}
                className="shrink-0 transition-transform duration-300 group-hover:scale-125"
              />
              <div className="min-w-0">
                <p className="font-display text-[11px] font-bold tracking-[0.14em] text-white leading-tight group-hover:underline underline-offset-4" style={{ textDecorationColor: v.color }}>
                  {v.title}
                </p>
                <p className="text-[10px] text-white/50 truncate group-hover:text-white/80 transition-colors duration-300">
                  {v.sub}
                </p>
              </div>
            </button>
          );
        })}
      </div>

      {createPortal(
        <AnimatePresence>
        {active && (
          <div
            className="fixed inset-0 z-[90] overflow-y-auto bg-[#0A0E17]/70 backdrop-blur-sm"
            onClick={() => setActive(null)}
            data-testid="topbar-modal-backdrop"
          >
            <div className="min-h-full flex justify-center items-start px-4 sm:px-6 pt-[112px] pb-10 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 8 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              onMouseLeave={() => setActive(null)}
              className="pointer-events-auto glass rounded-3xl border p-7 sm:p-9 max-w-2xl w-full"
              style={{
                borderColor: `${active.color}66`,
                background: `linear-gradient(165deg, ${GLOWS[active.id][0]}2E, rgba(10,14,23,0.94) 55%)`,
                boxShadow: `0 0 80px ${GLOWS[active.id][0]}44, 0 40px 120px rgba(0,0,0,0.6)`,
              }}
              data-testid="topbar-value-modal"
            >
              <div className="flex items-start justify-between gap-4 mb-5">
                <span
                  className="w-12 h-12 rounded-2xl flex items-center justify-center border shrink-0"
                  style={{ background: `${active.color}14`, borderColor: `${active.color}44` }}
                >
                  <active.icon size={22} style={{ color: active.color }} />
                </span>
                <button
                  onClick={() => setActive(null)}
                  data-testid="topbar-modal-close"
                  className="text-white/40 hover:text-white transition-colors duration-300"
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
              </div>
              <MediaSlot local={active.media} fallback={active.mediaFallback} alt={active.mediaAlt} color={active.color} />
              <p className="font-display text-[11px] font-bold tracking-[0.3em] mb-2" style={{ color: active.color }}>
                {active.title}
              </p>
              <h3 className="font-serif-lux text-3xl sm:text-4xl text-white leading-tight mb-3">{active.modalTitle}</h3>
              <p className="font-display text-[13px] font-semibold tracking-wide mb-7" style={{ color: active.color }}>
                {active.modalSub}
              </p>
              <div className="space-y-6">
                {active.sections.map((s, i) => (
                  <div key={s.heading} className="flex gap-4" data-testid={`topbar-modal-section-${i}`}>
                    <span
                      className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0"
                      style={{ background: active.color, boxShadow: `0 0 10px ${active.color}` }}
                    />
                    <div>
                      <h4 className="font-display text-sm font-bold text-white tracking-wide mb-1.5">
                        {s.heading}
                      </h4>
                      <p className="text-[13px] text-white/65 leading-[1.75]">{s.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
            </div>
          </div>
        )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};


